import React, { useMemo, useState } from "react";
import { SearchActionsToolbar } from "@/components/UI/DrilldownList";
import { DrilldownDepthContext } from "@/components/UI/DrilldownList/context/DrilldownDepthContext";
import { useLightbox } from "@/components/UI/lightbox";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { buildCascadeTree, getSelectedPathKeys } from "../utils/buildCascadeTree";
import { flattenForSearch } from "../utils/flattenForSearch";
import { StyledOuterWrapper } from "../styled";
import { ScrollWrapper, SectionLabel, SectionDivider } from "./PanelChrome";
import { PickerEmpty } from "./EmptyAndLoadingStates";
import { PanelRows, LeafList } from "./FolderPanel";

/** contentType → dataset title, for labelling Connected-Datasets rollup folders.
 *  Sourced from the react-query cache so no provider wiring is needed. */
const buildRollupDisplayMap = (allTpls) => {
  const map = {};
  for (const t of allTpls || []) {
    map[t.kp_content_type] = t.general?.content?.title || t.kp_content_type;
  }
  return map;
};

/**
 * Root panel of the cascade field picker.
 * - Global search (D5): a non-empty term replaces the sectioned view with a flat
 *   list of every matching leaf across the whole tree.
 * - Otherwise: sections (Annotations first, then Documents), each rendered via
 *   PanelRows (folder rows → nested panels, leaf runs → lists, labels → dividers).
 */
export const RootPanel = ({
  options,
  value,
  onSelect,
  emptyMessage = "No options available",
}) => {
  const { setOpen } = useLightbox() || {};
  const [search, setSearch] = useState("");
  const term = search.trim();

  const allTpls = useGetQueryData("allTpls");
  const platformConfigs = useGetQueryData("platformConfigs");
  const rollupDisplayMap = useMemo(() => buildRollupDisplayMap(allTpls), [allTpls]);
  // Split annotations into Thematics/Attributes only for self-managed tenants
  // running the non-legacy annotation mode — sourced here, not threaded in.
  // Split mode has no roles: sub-themes and Topics lead instead.
  const annoMode = getAnnoMode(platformConfigs);
  const isSplitMode = annoMode === "split";
  const annoSplitEnabled = !!(
    platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable &&
    annoMode !== "legacy"
  );

  const sections = useMemo(
    () => buildCascadeTree(options, { rollupDisplayMap, annoSplitEnabled, isSplitMode }),
    [options, rollupDisplayMap, annoSplitEnabled, isSplitMode]
  );

  const searchResults = useMemo(
    () => (term ? flattenForSearch(sections, term) : []),
    [sections, term]
  );

  // Folder keys leading to the current selection — marks those folders so you can
  // see which branch holds your pick when you reopen the picker.
  const selectedPathKeys = useMemo(
    () => getSelectedPathKeys(sections, value?.value),
    [sections, value]
  );

  const sectionsEmpty = sections.every((s) => (s.rows || []).length === 0);

  return (
    <DrilldownDepthContext.Provider value={0}>
      <StyledOuterWrapper>
        <SearchActionsToolbar
          search={{ value: search, onChange: setSearch }}
          onClose={setOpen ? () => setOpen(false) : undefined}
        />
        <ScrollWrapper style={{ flex: 1, marginTop: 4 }}>
          {term ? (
            <LeafList
              leaves={searchResults}
              value={value}
              onSelect={onSelect}
              emptyMessage="No results found"
            />
          ) : sectionsEmpty ? (
            <PickerEmpty message={emptyMessage} />
          ) : (
            sections.map((section, i) => (
              <React.Fragment key={section.label || `section-${i}`}>
                {i > 0 && <SectionDivider />}
                <SectionLabel>{section.label}</SectionLabel>
                <PanelRows rows={section.rows} value={value} onSelect={onSelect} selectedPathKeys={selectedPathKeys} />
              </React.Fragment>
            ))
          )}
        </ScrollWrapper>
      </StyledOuterWrapper>
    </DrilldownDepthContext.Provider>
  );
};
